/**
 * Isomorphic formatting utilities
 * These functions work identically in Node.js and Browser environments
 * IMPORTANT: Only pure functions without runtime-specific dependencies
 */

// Constants moved to backend/platform/constants/ - using local constants for safety
const CURRENCY = {
  USD: 'USD',
  DEFAULT_LOCALE: 'en-US',
  DEFAULT_CURRENCY: 'USD',
  DEFAULT: 'USD',
  PRECISION: {
    FIAT: 2,
    CRYPTO: 8,
  },
} as const

const SAFE_DATE_FORMATS = {
  ISO: 'YYYY-MM-DD',
  DISPLAY: 'DD/MM/YYYY',
  US: 'MM/DD/YYYY',
} as const

/**
 * Format currency amounts with proper localization
 * Uses Intl.NumberFormat - available in both Node.js and browsers
 */
export function formatCurrency(
  amount: number,
  currency: string = CURRENCY.DEFAULT,
  locale: string = 'en-US',
): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: CURRENCY.PRECISION.FIAT,
      maximumFractionDigits: CURRENCY.PRECISION.FIAT,
    }).format(amount)
  } catch (_error) {
    // Fallback for invalid currency codes
    return `${currency.toUpperCase()} ${amount.toFixed(CURRENCY.PRECISION.FIAT)}`
  }
}

/**
 * Format crypto amounts with appropriate precision
 */
export function formatCrypto(amount: number, symbol: string, locale: string = 'en-US'): string {
  try {
    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: CURRENCY.PRECISION.CRYPTO,
    }).format(amount)
    return `${formatted} ${symbol.toUpperCase()}`
  } catch (_error) {
    return `${amount.toFixed(CURRENCY.PRECISION.CRYPTO)} ${symbol.toUpperCase()}`
  }
}

/**
 * Format percentages with localization
 */
export function formatPercentage(
  value: number,
  decimals: number = 2,
  locale: string = 'en-US',
): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value / 100)
  } catch (_error) {
    return `${value.toFixed(decimals)}%`
  }
}

/**
 * Format dates consistently across platforms
 * Uses native Date methods that work in both environments
 */
export function formatDate(
  date: Date | string | number,
  format: keyof typeof SAFE_DATE_FORMATS = 'DISPLAY',
): string {
  const dateObj = new Date(date)

  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date'
  }

  const formatString = SAFE_DATE_FORMATS[format]

  // Simple format replacement - works in both Node.js and Browser
  return formatString
    .replace('YYYY', dateObj.getFullYear().toString())
    .replace('MM', (dateObj.getMonth() + 1).toString().padStart(2, '0'))
    .replace('DD', dateObj.getDate().toString().padStart(2, '0'))
    .replace('HH', dateObj.getHours().toString().padStart(2, '0'))
    .replace('mm', dateObj.getMinutes().toString().padStart(2, '0'))
    .replace('ss', dateObj.getSeconds().toString().padStart(2, '0'))
    .replace('sss', dateObj.getMilliseconds().toString().padStart(3, '0'))
}

/**
 * Format relative time (e.g., "2 hours ago")
 * Uses Intl.RelativeTimeFormat when available
 */
export function formatRelativeTime(date: Date | string | number, locale: string = 'en-US'): string {
  const now = new Date()
  const dateObj = new Date(date)
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000)

  // Use Intl.RelativeTimeFormat if available (modern browsers and Node.js)
  if (typeof Intl !== 'undefined' && 'RelativeTimeFormat' in Intl) {
    try {
      const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })

      if (Math.abs(diffInSeconds) < 60) {
        return rtf.format(-diffInSeconds, 'second')
      }
      if (Math.abs(diffInSeconds) < 3600) {
        return rtf.format(-Math.floor(diffInSeconds / 60), 'minute')
      }
      if (Math.abs(diffInSeconds) < 86400) {
        return rtf.format(-Math.floor(diffInSeconds / 3600), 'hour')
      }
      return rtf.format(-Math.floor(diffInSeconds / 86400), 'day')
    } catch (_error) {
      // Fall through to manual implementation
    }
  }

  // Fallback for older environments
  const absDiff = Math.abs(diffInSeconds)
  const isFuture = diffInSeconds < 0

  if (absDiff < 60) {
    return isFuture ? 'in a few seconds' : 'a few seconds ago'
  }
  if (absDiff < 3600) {
    const minutes = Math.floor(absDiff / 60)
    return isFuture ? `in ${minutes} minutes` : `${minutes} minutes ago`
  }
  if (absDiff < 86400) {
    const hours = Math.floor(absDiff / 3600)
    return isFuture ? `in ${hours} hours` : `${hours} hours ago`
  }

  const days = Math.floor(absDiff / 86400)
  return isFuture ? `in ${days} days` : `${days} days ago`
}

/**
 * Format file sizes in human readable format
 */
export function formatFileSize(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`
}

/**
 * Truncate text with ellipsis
 */
export function truncateText(text: string, maxLength: number, ellipsis: string = '...'): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength - ellipsis.length) + ellipsis
}

/**
 * Format addresses with proper line breaks and formatting
 */
export function formatAddress(address: {
  line1?: string
  line2?: string
  city?: string
  state?: string
  postalCode?: string
  country?: string
}): string {
  const parts = [
    address.line1,
    address.line2,
    [address.city, address.state].filter(Boolean).join(', '),
    address.postalCode,
    address.country,
  ].filter(Boolean)

  return parts.join('\n')
}

/**
 * Parse date string with format detection
 */
export const parseDate = (dateString: string): Date => {
  if (!dateString) return new Date(NaN)

  // Try ISO format first
  const isoDate = new Date(dateString)
  if (!isNaN(isoDate.getTime())) {
    return isoDate
  }

  // Try common formats
  const formats = [
    /^(\d{1,2})\.(\d{1,2})\.(\d{4})/, // DD.MM.YYYY
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})/, // DD/MM/YYYY or MM/DD/YYYY
    /^(\d{4})-(\d{1,2})-(\d{1,2})/, // YYYY-MM-DD
  ]

  for (const format of formats) {
    const match = dateString.match(format)
    if (match) {
      const [, part1, part2, part3] = match

      // Determine if it's DD/MM/YYYY or MM/DD/YYYY based on values
      if (format.source.includes('\\.')) {
        // DD.MM.YYYY format
        return new Date(parseInt(part3), parseInt(part2) - 1, parseInt(part1))
      } else if (format.source.includes('-')) {
        // YYYY-MM-DD format
        return new Date(parseInt(part1), parseInt(part2) - 1, parseInt(part3))
      } else {
        // Ambiguous MM/DD/YYYY vs DD/MM/YYYY - assume MM/DD/YYYY for US locale
        return new Date(parseInt(part3), parseInt(part1) - 1, parseInt(part2))
      }
    }
  }

  return new Date(NaN)
}

/**
 * Hardcoded alias → canonical ID map for offline normalization.
 * The backend PlatformRegistryService loads the full map from the DB;
 * this is the isomorphic fallback for frontend and transformers.
 */
const PLATFORM_ALIAS_MAP: Record<string, string> = {
  'gate.io': 'gate_io',
  gateio: 'gate_io',
  'gate-io': 'gate_io',
  'gate io': 'gate_io',
  'crypto.com': 'crypto_com',
  cryptocom: 'crypto_com',
  'crypto-com': 'crypto_com',
  okex: 'okx',
  'ok-ex': 'okx',
  htx: 'huobi',
  'ku-coin': 'kucoin',
  ku_coin: 'kucoin',
  'hit-btc': 'hitbtc',
  hit_btc: 'hitbtc',
  'mexc-global': 'mexc',
  'coin-tracking': 'cointracking',
  coin_tracking: 'cointracking',
  'csv-upload': 'csv_upload',
  'csv upload': 'csv_upload',
  'manual-entry': 'manual_entry',
  'manual entry': 'manual_entry',
  manual: 'manual_entry',
}

/**
 * Hardcoded canonical ID → display name map.
 * Covers platforms whose display name can't be derived by simple titleCase.
 */
const PLATFORM_DISPLAY_MAP: Record<string, string> = {
  gate_io: 'Gate.io',
  kucoin: 'KuCoin',
  hitbtc: 'HitBTC',
  mexc: 'MEXC',
  okx: 'OKX',
  crypto_com: 'Crypto.com',
  csv_upload: 'CSV Upload',
  manual_entry: 'Manual Entry',
  cointracking: 'CoinTracking',
  pancakeswap: 'PancakeSwap',
  'binance-futures': 'Binance Futures',
  // ⚠️ BROWSER-MEASURED (2026-08-09): these four rendered as «Ledger-live»,
  // «Swissborg», «Coinbase-pro» and «Kucoin-multi» on surfaces that cannot
  // reach the exchange catalogue (attribute mappings, needs-attention tray,
  // asset aliases). The catalogue calls them «Ledger Live», «SwissBorg»,
  // «Coinbase Pro» and «KuCoin Multi» — the fallback splits on `_` only, so a
  // hyphen or an internal capital survives as-is.
  'ledger-live': 'Ledger Live',
  swissborg: 'SwissBorg',
  'coinbase-pro': 'Coinbase Pro',
  'kucoin-multi': 'KuCoin Multi',
  // ⚠️ BROWSER-MEASURED (2026-08-14): la tabla de fuentes de «Importar»
  // enseñaba el slug crudo bajo el nombre de cada fuente. Al pasarla por este
  // formateador, estos cuatro salían «Ascendex», «Coinex», «Probit» y
  // «Cryptocom-exchange»; el catálogo de exchanges los llama así:
  ascendex: 'AscendEX',
  coinex: 'CoinEx',
  probit: 'ProBit',
  'cryptocom-exchange': 'Crypto.com Exchange',
}

/**
 * Normalize a raw platform string to its canonical ID.
 * Checks the hardcoded alias map first, then applies a general rule:
 * trim, lowercase, replace separators with underscores.
 */
/**
 * ⚠️⚠️ El centinela de «medio no identificado» que estampa el importador.
 *
 * Los guiones bajos DOBLES no son decorativos: marcan que esto no es un
 * exchange, y toda la app lo reconoce por su forma exacta para pintar «Sin
 * identificar» en lugar de un concepto interno en inglés.
 */
export const UNIDENTIFIED_PLATFORM_SLUG = '__unknown__'

export function normalizePlatformName(platform: string | null | undefined): string | null {
  if (!platform?.trim()) return null
  const lower = platform.trim().toLowerCase()

  // ⚠️⚠️ El centinela sale INTACTO. La regla general de abajo colapsa los
  // guiones repetidos y quita el final, así que `__unknown__` se convertía en
  // `_unknown` — una forma que ningún resolutor reconoce.
  //
  // Medido por la UI el 2026-08-14: la página de Balances mostraba «Unknown»
  // en TRES sitios (cabecera del grupo, desplegable de medios y «Resumen de
  // Totales») para la MISMA transacción que Traza y la rejilla llaman «Sin
  // identificar» — porque a Balances le llegaba el slug mutilado.
  if (lower === UNIDENTIFIED_PLATFORM_SLUG) return UNIDENTIFIED_PLATFORM_SLUG

  if (PLATFORM_ALIAS_MAP[lower]) {
    return PLATFORM_ALIAS_MAP[lower]
  }

  // General rule: replace dots, hyphens, spaces with underscores, collapse, trim trailing
  return lower
    .replace(/[.\-\s]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/_$/, '')
}

/**
 * Get a human-friendly display name for a canonical platform ID.
 * Checks the hardcoded display map, then falls back to titleCase.
 */
export function formatPlatformDisplay(platform: string): string {
  if (PLATFORM_DISPLAY_MAP[platform]) {
    return PLATFORM_DISPLAY_MAP[platform]
  }
  // Fallback: split on underscores, HYPHENS and spaces, titleCase each word.
  // Filter empties so leading/trailing/double underscores don't surface as
  // leading/trailing/double spaces in the display name — e.g. `_unknown` was
  // rendering as `" Unknown"` (with a leading space) because `split('_')`
  // produces `["", "unknown"]`.
  //
  // ⚠️⚠️ Partir SÓLO por `_` es la razón de que el mapa de arriba haya crecido a
  // golpe de medición: los comentarios que lo acompañan dicen literalmente que
  // «un guion o una mayúscula interna sobreviven». Con el guion y el espacio
  // dentro, `kraken-futures` da «Kraken Futures» y `arbitrum one` —la forma en
  // que el backend guarda la cadena de destino de un bridge— da «Arbitrum One»
  // en lugar de «Arbitrum one». Las entradas del mapa siguen mandando, así que
  // ninguna etiqueta ya afinada cambia.
  return platform
    .split(/[_\s-]+/)
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}
